import _ = require("underscore");
import moment = require("moment");
import "rxjs/add/operator/toPromise";
import {Http, Headers, Response} from "@angular/http";
import {GlobalService} from "../GlobalService";
import {RequestOptionsArgs} from "@angular/http/src/interfaces";
import {AuthorizationHelperService} from "../Authorization/AuthorizationHelperService";
import {AppSettings} from "../AppSettings";

export class HttpServiceBase implements NPS.Common.IHttpServiceBase {
    public static urlSeparator = "/";
    private serviceUrl: string;
    private queryString: string;
    private isPageReloading = false;
    private requests: Promise<any>[] = [];
    cache: NPS.Common.ITypedObject<any>;

    constructor(
        private http: Http,
        private globalService: GlobalService,
        private authorizationHelperService: AuthorizationHelperService,
        private methodServiceUrl?: string,
        private alternativeServiceUrl?: string
    ) {
        // Allow derived service to configure endpoint other than current host
        this.serviceUrl = alternativeServiceUrl ?
            HttpServiceBase.getServiceUrl(alternativeServiceUrl, methodServiceUrl) :
            HttpServiceBase.getServiceUrl(AppSettings.systemConfiguration.ApiAddress, methodServiceUrl);

        this.queryString = globalService.window.location.search;
        if (!this.cache) {
            globalService.cacheFactory.set("Service" + (methodServiceUrl || ""), {});
        }
        this.cache = globalService.cacheFactory.storage;

        globalService.window.addEventListener("beforeunload", () => {
            this.isPageReloading = true;
        });
    }

    static getServiceUrl(serviceUrl: string, methodServiceUrl?: string): string {
        var url = serviceUrl;
        if (methodServiceUrl && methodServiceUrl.length > 0) {
            if (methodServiceUrl[0] === HttpServiceBase.urlSeparator) {
                methodServiceUrl = methodServiceUrl.substring(HttpServiceBase.urlSeparator.length);
            }
            if (methodServiceUrl[methodServiceUrl.length - HttpServiceBase.urlSeparator.length] !== HttpServiceBase.urlSeparator) {
                methodServiceUrl += HttpServiceBase.urlSeparator;
            }
            url += methodServiceUrl;
        }

        return url;
    }

    static getParamsString(queryString: string, params?: NPS.Common.ITypedObject<any>): string {
        var result: string = queryString || "";

        if (params) {
            if (result) {
                result += "&";
            } else {
                result = "?";
            }

            //To make complex objects work with FromUri, not compatible with nesting
            HttpServiceBase.formatParams(params);

            let queryStringPartials: string[] = [];
            Object.keys(params)
                .filter((key: string) => params[key] !== null && params[key] !== undefined)
                .forEach(key => {
                    let value = params[key];
                    if (Array.isArray(value)) {
                        (<Array<any>>value).forEach(item => {
                            queryStringPartials.push(HttpServiceBase.getQueryStringPartial(key, item));
                        })
                    } else {
                        queryStringPartials.push(HttpServiceBase.getQueryStringPartial(key, value));
                    }
                })

            result += queryStringPartials.join("&");
        }

        return result;
    }

    private static getQueryStringPartial(key: string, value: any) {
        return `${HttpServiceBase.encodeParamsString(key)}=${HttpServiceBase.encodeParamsString(value)}`;
    }

    static formatParams(params: NPS.Common.ITypedObject<any>): void {
        var keys: string[] = Object.keys(params);

        while (keys.length > 0) {
            var key: string = keys.shift();
            var value: any = params[key];
            
            if (value !== null && value !== undefined) {
                if (moment.isMoment(value)) {
                    params[key] = (value).toISOString();
                } else if (typeof value === "object" && !Array.isArray(value)) {
                    if (typeof value.toJson === "function") {
                        value = value.toJson();
                    }
                    Object.keys(value).forEach((valueKey: string) => {
                        keys.push(key + "." + valueKey);
                        params[key + "." + valueKey] = value[valueKey];
                    });
                    delete params[key];
                }
            }
        }
    }

    static encodeParamsString(value: any): string {
        return encodeURIComponent(value).
            replace(/%40/gi, "@").
            replace(/%3A/gi, ":").
            replace(/%24/g, "$").
            replace(/%2C/gi, ",");
    }

    afterLogout(event: any): void {
        console.warn("HttpServiceBase.afterLogout() is deprecated.");
        var requests: Promise<any>[] = this.requests.slice(0);

        Promise.all(requests).then(() => {
            this.cache = {};
        });
    }

    get<T>(): Promise<T>;
    get<T>(name: string): Promise<T>;
    get<T>(name: string, params: any): Promise<T>;
    get<T>(name: string, params: any, config: RequestOptionsArgs): Promise<T>;
    get<T>(name: string, params: any, config: RequestOptionsArgs, raiseMessageOnError: boolean): Promise<T>;
    get<T>(name?: string, params?: any, config?: RequestOptionsArgs, raiseMessageOnError: boolean = true): Promise<T> {
        if (!config) {
            config = {};
        }

        if (params) {
            _.extend(config, { params: params });
        }

        return this.call<T>(name, "GET", config, raiseMessageOnError);
    }

    post<T>(name: string, data: any): Promise<T>;
    post<T>(name: string, data: any, params: any): Promise<T>;
    post<T>(name: string, data: any, params: any, config: RequestOptionsArgs): Promise<T>;
    post<T>(name: string, data: any, params: any, config: RequestOptionsArgs, raiseMessageOnError: boolean): Promise<T>;
    post<T>(name: string, data: any, params?: any, config?: RequestOptionsArgs, raiseMessageOnError: boolean = true): Promise<T> {
        if (!config) {
            config = {};
        }

        if (data) {
            _.extend(config, { body: data });
        }

        if (params) {
            _.extend(config, { params: params });
        }

        return this.call<T>(name, "POST", config, raiseMessageOnError);
    }

    put<T>(name: string, data: any): Promise<T>;
    put<T>(name: string, data: any, params: any): Promise<T>;
    put<T>(name: string, data: any, params: any, config: RequestOptionsArgs): Promise<T>;
    put<T>(name: string, data: any, params: any, config: RequestOptionsArgs, raiseMessageOnError: boolean): Promise<T>;
    put<T>(name: string, data: any, params?: any, config?: RequestOptionsArgs, raiseMessageOnError: boolean = true): Promise<T> {
        if (!config) {
            config = {};
        }

        if (data) {
            _.extend(config, { body: data });
        }

        if (params) {
            _.extend(config, { params: params });
        }

        return this.call<T>(name, "PUT", config, raiseMessageOnError);
    }

    delete<T>(name: string, data: any): Promise<T>;
    delete<T>(name: string, data: any, params: any): Promise<T>;
    delete<T>(name: string, data: any, params: any, config: RequestOptionsArgs): Promise<T>;
    delete<T>(name: string, data: any, params: any, config: RequestOptionsArgs, raiseMessageOnError: boolean): Promise<T>;
    delete<T>(name: string, data: any, params?: any, config?: RequestOptionsArgs, raiseMessageOnError: boolean = true): Promise<T> {
        if (!config) {
            config = {};
        }

        if (data) {
            _.extend(config, { body: data });
        }

        if (params) {
            _.extend(config, { params: params });
        }

        return this.call<T>(name, "DELETE", config, raiseMessageOnError);
    }

    call<T>(name: string, method: string): Promise<T>;
    call<T>(name: string, method: string, config: RequestOptionsArgs): Promise<T>;
    call<T>(name: string, method: string, config: RequestOptionsArgs, raiseMessageOnError: boolean): Promise<T>;
    call<T>(name: string, method: string, config?: RequestOptionsArgs, raiseMessageOnError: boolean = true): Promise<T> {
        var requestConfig = this.buildConfig(name, method, config);
        var request = this.performRequest(requestConfig);

        var result = request.then(response => {
            var result = this.extractData(response);
            return Promise.resolve<T>(result);
        }).catch(this.handleError);

        return result;
    }

    invalidateCache(name: string, params?: any) {
        var url: string = this.getMethodUrl(name);

        if (params) {
            url += this.getParamsString(params);
        }

        if (this.cache && this.cache[url]) {
            delete this.cache[url];
        }
    }

    getParamsString(params?: any): string {
        return HttpServiceBase.getParamsString(this.queryString, params);
    }

    getMethodUrl(name: string) {
        var methodUrl: string = this.serviceUrl;
        if (name) {
            methodUrl += name;
        }
        return methodUrl;
    }

    extractData(res: Response): any {
        let result;
        try {
            result = res.json();
        } catch (err) {
            if (res.status && res.statusText) {
                result = {
                    status: res.status,
                    statusText: res.statusText
                };
            } else {
                console.error(err);
            }
        }
        return result || {};
    }

    performRequest(fullConfig: RequestOptionsArgs): Promise<any> {
        return this.http.request(fullConfig.url, fullConfig).toPromise();
    }

    buildConfig(name: string, method: string, config?: RequestOptionsArgs) {
        let url: string = this.getMethodUrl(name);
        let resultConfig: RequestOptionsArgs = { method: method, url: url };
        if (!config) {
            config = {};
        }

        _.extend(resultConfig, config);
        resultConfig.url += this.getParamsString(resultConfig.search);
        delete resultConfig.search;

        resultConfig.headers = new Headers(resultConfig.headers);
        resultConfig.headers.append("Authorization", `Bearer ${this.authorizationHelperService.getToken()}`);

        return resultConfig;
    }

    handleError(error: any) {
        let errMsg = "Server error", responseBody;
        try {
            responseBody = JSON.parse(error._body);
            if (responseBody.Message) {
                errMsg = responseBody.Message;
            } else if (responseBody.message) {
                errMsg = responseBody.message;
            }
        } catch (e) {
            if (error._body && !/<[a-z][\s\S]*>/i.test(error._body)) {
                errMsg = error._body;
            }
        }
        errMsg += error.status ? ` (Status: ${error.status} - ${error.statusText})` : "";
        errMsg += error.url ? ` URL: "${error.url}"` : "";
        return Promise.reject(errMsg);
    }
}



// WEBPACK FOOTER //
// ./~/@nordpoolgroup/nps-angular-2-common/src/HttpService/HttpServiceBase.ts